import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import {
  Wallet, Users, Calendar, MapPin, TrendingUp, Trophy, Ship, Compass,
  ArrowRight, X, Star,
} from "lucide-react";
import {
  BOAT_LABELS, GROUP_LABELS, EXP_LABELS, fmtEur, formatPeriod, STATUS_META, fmtDate,
} from "@/admin/adminLabels";

const OPP_COLORS = {
  ALTA: { bg: "#DC2626", text: "#FFFFFF", label: "Opportunità ALTA" },
  MEDIA: { bg: "#F59E0B", text: "#FFFFFF", label: "Opportunità MEDIA" },
  PREMIUM: { bg: "#C4A26C", text: "#0B1A2D", label: "Opportunità PREMIUM" },
  EMERGENTE: { bg: "#0EA5E9", text: "#FFFFFF", label: "Emergente" },
};

/* Drill-down side panel showing leads */
function LeadsSidePanel({ open, onClose, title, leadIds, onSelectLead }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !leadIds || leadIds.length === 0) {
      setLeads([]);
      return;
    }
    let cancel = false;
    setLoading(true);
    api.post("/admin/leads/by-ids", { ids: [...new Set(leadIds)] })
      .then(r => { if (!cancel) setLeads(r.data || []); })
      .catch(() => {})
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [open, leadIds]);

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent data-testid="market-drill-panel" side="right" className="w-full sm:max-w-xl p-0 bg-white">
        <SheetHeader className="px-6 py-5 border-b border-[#E2E8F0]">
          <SheetTitle className="font-serif text-xl text-navy">{title}</SheetTitle>
          <p className="text-sm text-ink/70">{leadIds?.length || 0} richieste · clicca una richiesta per aprire la scheda completa</p>
        </SheetHeader>
        <div className="overflow-y-auto" style={{ maxHeight: "calc(100vh - 90px)" }}>
          {loading ? (
            <p className="p-6 text-center text-ink/60">Caricamento…</p>
          ) : leads.length === 0 ? (
            <p className="p-6 text-center text-ink/60">Nessuna richiesta.</p>
          ) : leads.map(l => {
            const status = STATUS_META[l.status || "nuova_richiesta"];
            const period = formatPeriod(l);
            return (
              <button
                key={l.id}
                data-testid={`drill-lead-${l.id}`}
                onClick={() => { onSelectLead(l.id); onClose(); }}
                className="w-full text-left px-6 py-4 border-b border-[#F1F5F9] hover:bg-sand/60 transition flex items-start gap-3"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    {l.da_contattare_oggi && <span className="h-1.5 w-1.5 rounded-full bg-gold" />}
                    <p className="font-medium text-navy">{l.name}</p>
                    {status && <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full" style={{ background: status.bg, color: status.color }}>{status.label}</span>}
                  </div>
                  <p className="text-xs text-ink/70 mt-1 truncate">{l.email} · {(l.destinations || []).join(" / ")}</p>
                  <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-ink">
                    <span>{fmtEur(l.budget)}</span>
                    <span>{l.num_participants} pax</span>
                    <span>{BOAT_LABELS[l.boat_type] || l.boat_type}</span>
                    {period.badge !== "—" && (
                      <span className="text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-full" style={{ background: period.badgeBg, color: period.badgeColor }}>
                        {period.badge}
                      </span>
                    )}
                  </div>
                  {(l.experiences || []).length > 0 && (
                    <p className="text-[10px] text-ink/60 mt-1">{(l.experiences || []).map(e => EXP_LABELS[e] || e).join(", ")}</p>
                  )}
                </div>
                <ArrowRight className="h-4 w-4 text-ink/40 mt-1" />
              </button>
            );
          })}
        </div>
      </SheetContent>
    </Sheet>
  );
}

/* --- Clickable wrappers --- */
function ClickableBar({ entry, total, onClick, labelResolver }) {
  const pct = total > 0 ? (entry.count / total) * 100 : 0;
  return (
    <button
      data-testid={`bar-${entry.key}`}
      onClick={onClick}
      className="w-full text-left flex items-center gap-3 py-1.5 hover:bg-sand/60 rounded-md px-2 -mx-2 transition group"
    >
      <span className="text-sm text-navy w-1/3 truncate">{labelResolver ? labelResolver(entry.key) : entry.key}</span>
      <div className="flex-1 h-1.5 bg-sand rounded-full overflow-hidden">
        <div className="h-full bg-navy group-hover:bg-gold transition" style={{ width: `${pct}%` }} />
      </div>
      <span className="text-sm font-semibold text-navy w-10 text-right">{entry.count}</span>
      <ArrowRight className="h-3 w-3 text-ink/30 group-hover:text-navy" />
    </button>
  );
}

function KpiTile({ icon: Icon, eyebrow, title, subtitle, hint }) {
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-turquoise" />
        <span className="text-[11px] uppercase tracking-wider text-ink/70">{eyebrow}</span>
      </div>
      <p className="mt-3 font-serif text-3xl text-navy leading-none">{title}</p>
      {subtitle && <p className="mt-1 text-sm text-ink/70">{subtitle}</p>}
      {hint && <div className="mt-3 space-y-1">{hint}</div>}
    </div>
  );
}

/* --- Main --- */
export default function MarketPanel({ onSelectLead }) {
  const [data, setData] = useState(null);
  const [drill, setDrill] = useState({ open: false, title: "", ids: [] });

  useEffect(() => {
    api.get("/admin/market-insights").then(r => setData(r.data)).catch(() => {});
  }, []);

  const openDrill = (title, ids) => setDrill({ open: true, title, ids });
  const closeDrill = () => setDrill({ ...drill, open: false });

  if (!data) return <div className="text-ink">Caricamento…</div>;

  const { kpi, periods, destinations, budget_brackets, boat_types, group_types, experiences, opportunities, total_requests } = data;

  const sortedExp = experiences.slice(0, 10);
  const medals = ["🥇", "🥈", "🥉"];

  return (
    <div className="space-y-10" data-testid="market-panel">
      {/* KPI principali */}
      <section>
        <p className="label-eyebrow">KPI Principali</p>
        <h2 className="mt-2 font-serif text-2xl text-navy">Panoramica della domanda</h2>
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <KpiTile icon={Wallet} eyebrow="Budget medio"
            title={fmtEur(kpi.avg_budget)} subtitle={`su ${total_requests} richieste totali`} />
          <KpiTile icon={Users} eyebrow="Partecipanti medi"
            title={`${kpi.avg_participants} persone`}
            hint={Object.entries(kpi.group_breakdown).slice(0, 4).map(([k, pct]) => (
              <div key={k} className="flex justify-between text-xs text-ink">
                <span>{GROUP_LABELS[k] || k}</span>
                <span className="font-semibold text-navy">{pct}%</span>
              </div>
            ))} />
          <KpiTile icon={Calendar} eyebrow="Periodo preferito"
            title={kpi.period_preferred?.month || "—"}
            subtitle={kpi.period_preferred ? `${kpi.period_preferred.pct}% delle richieste` : ""} />
          <KpiTile icon={MapPin} eyebrow="Destinazione preferita"
            title={kpi.destination_preferred?.destination || "—"}
            subtitle={kpi.destination_preferred ? `${kpi.destination_preferred.pct}% delle richieste` : ""} />
        </div>
      </section>

      {/* Analisi del periodo */}
      <section>
        <p className="label-eyebrow">Analisi del periodo</p>
        <h2 className="mt-2 font-serif text-2xl text-navy">Quando vogliono partire</h2>
        <div className="mt-5 grid md:grid-cols-3 gap-4">
          {/* Flessibile */}
          <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5">
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full" style={{ background: "#DBEAFE", color: "#0B1A2D" }}>Sono flessibile</span>
            </div>
            <div className="mt-4 space-y-1">
              {periods.flexible.length === 0 && <p className="text-xs text-ink/60">Nessuna richiesta flessibile</p>}
              {periods.flexible.slice(0, 8).map((it) => (
                <button key={it.key} data-testid={`flex-${it.key}`}
                        onClick={() => openDrill(`Flessibile · ${it.key}`, it.lead_ids)}
                        className="w-full text-left flex items-center justify-between px-2 py-1.5 -mx-2 rounded-md hover:bg-sand/60 transition group">
                  <span className="text-sm text-navy truncate">{it.key}</span>
                  <span className="flex items-center gap-1 shrink-0">
                    <span className="text-sm font-semibold text-navy">{it.count}</span>
                    <ArrowRight className="h-3 w-3 text-ink/30 group-hover:text-navy" />
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Date precise */}
          <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5">
            <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full" style={{ background: "#FEF3C7", color: "#0B1A2D" }}>Date precise</span>
            <div className="mt-4 space-y-1">
              {periods.precise.length === 0 && <p className="text-xs text-ink/60">Nessuna richiesta con date precise</p>}
              {periods.precise.slice(0, 8).map((it) => (
                <button key={it.key} data-testid={`prec-${it.key}`}
                        onClick={() => openDrill(`Date precise · ${it.key}`, it.lead_ids)}
                        className="w-full text-left flex items-center justify-between px-2 py-1.5 -mx-2 rounded-md hover:bg-sand/60 transition group">
                  <span className="text-sm text-navy truncate">{it.key}</span>
                  <span className="flex items-center gap-1 shrink-0">
                    <span className="text-sm font-semibold text-navy">{it.count}</span>
                    <ArrowRight className="h-3 w-3 text-ink/30 group-hover:text-navy" />
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Best deals */}
          <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5">
            <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full" style={{ background: "#C4A26C", color: "#FFFFFF" }}>Le migliori occasioni</span>
            <button data-testid="best-deals-btn"
                    onClick={() => openDrill("Le migliori occasioni", periods.best_deals.lead_ids)}
                    disabled={periods.best_deals.count === 0}
                    className="w-full mt-6 text-left rounded-lg hover:bg-sand/60 transition p-3 -m-3 disabled:opacity-50 disabled:cursor-not-allowed">
              <p className="font-serif text-3xl text-navy">{periods.best_deals.count}</p>
              <p className="text-xs text-ink/70 mt-1">richieste · {periods.best_deals.pct}% del totale</p>
              <p className="text-[10px] text-ink/60 mt-2">Cliente aperto a qualsiasi periodo conveniente</p>
            </button>
          </div>
        </div>
      </section>

      {/* Opportunità commerciali */}
      <section>
        <p className="label-eyebrow">Opportunità commerciali</p>
        <h2 className="mt-2 font-serif text-2xl text-navy">Dove puntare ora</h2>
        <p className="mt-1 text-sm text-ink/70">Combinazioni destinazione × mese aggregate dalle richieste. Più alta è la concentrazione, più realistico è organizzare un cluster.</p>
        <div className="mt-5 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {opportunities.length === 0 && <p className="text-sm text-ink/60">Nessuna opportunità identificata.</p>}
          {opportunities.map((o, i) => {
            const c = OPP_COLORS[o.opportunity_level] || OPP_COLORS.EMERGENTE;
            return (
              <button key={i} data-testid={`opportunity-${o.destination}-${o.month}`}
                      onClick={() => openDrill(`${o.destination} · ${o.month}`, o.lead_ids)}
                      className="text-left bg-white border border-[#E2E8F0] rounded-2xl p-5 hover:border-navy/40 hover:shadow-md transition">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-serif text-lg text-navy">{o.destination} · {o.month}</p>
                  <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full whitespace-nowrap"
                        style={{ background: c.bg, color: c.text }}>{c.label}</span>
                </div>
                <p className="mt-3 font-serif text-2xl text-navy">{o.count} richieste</p>
                <div className="mt-2 text-xs text-ink space-y-0.5">
                  <p>Budget medio: <span className="font-semibold text-navy">{fmtEur(o.avg_budget)}</span></p>
                  <p>Tipologia prevalente: <span className="font-semibold text-navy">{BOAT_LABELS[o.prevalent_boat_type] || o.prevalent_boat_type || "—"}</span></p>
                </div>
              </button>
            );
          })}
        </div>
      </section>

      {/* Destinazioni + Fasce budget */}
      <section className="grid md:grid-cols-2 gap-4">
        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5">
          <p className="font-medium text-navy">Destinazioni più richieste</p>
          <div className="mt-4 space-y-1">
            {destinations.slice(0, 12).map((d) => (
              <ClickableBar key={d.key} entry={d} total={total_requests}
                            onClick={() => openDrill(`Destinazione · ${d.key}`, d.lead_ids)} />
            ))}
          </div>
        </div>
        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5">
          <p className="font-medium text-navy">Fasce di budget</p>
          <div className="mt-4 space-y-1">
            {budget_brackets.map((b) => (
              <ClickableBar key={b.key} entry={b} total={total_requests}
                            onClick={() => openDrill(`Budget · ${b.key}`, b.lead_ids)} />
            ))}
          </div>
        </div>
      </section>

      {/* Tipo barca + Tipologia gruppo */}
      <section className="grid md:grid-cols-2 gap-4">
        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5">
          <p className="font-medium text-navy">Tipo barca</p>
          <div className="mt-4 space-y-1">
            {boat_types.map((b) => (
              <ClickableBar key={b.key} entry={b} total={total_requests}
                            labelResolver={(k) => BOAT_LABELS[k] || k}
                            onClick={() => openDrill(`Tipo barca · ${BOAT_LABELS[b.key] || b.key}`, b.lead_ids)} />
            ))}
          </div>
        </div>
        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5">
          <p className="font-medium text-navy">Tipologia gruppo</p>
          <div className="mt-4 space-y-1">
            {group_types.map((g) => (
              <ClickableBar key={g.key} entry={g} total={total_requests}
                            labelResolver={(k) => GROUP_LABELS[k] || k}
                            onClick={() => openDrill(`Gruppo · ${GROUP_LABELS[g.key] || g.key}`, g.lead_ids)} />
            ))}
          </div>
        </div>
      </section>

      {/* Esperienze ranking */}
      <section>
        <p className="label-eyebrow">Esperienze cercate · Ranking</p>
        <h2 className="mt-2 font-serif text-2xl text-navy">Cosa cercano i clienti</h2>
        <div className="mt-5 bg-white border border-[#E2E8F0] rounded-2xl p-5">
          {sortedExp.length === 0 && <p className="text-sm text-ink/60">Nessuna esperienza selezionata.</p>}
          <div className="space-y-1">
            {sortedExp.map((exp, idx) => (
              <button key={exp.key} data-testid={`exp-${exp.key}`}
                      onClick={() => openDrill(`Esperienza · ${EXP_LABELS[exp.key] || exp.key}`, exp.lead_ids)}
                      className="w-full text-left flex items-center gap-3 px-2 py-2.5 -mx-2 rounded-md hover:bg-sand/60 transition group">
                <span className="text-2xl w-8 text-center">{medals[idx] || `${idx + 1}.`}</span>
                <span className="flex-1 font-medium text-navy">{EXP_LABELS[exp.key] || exp.key}</span>
                <span className="text-sm text-ink/70">{exp.count} richieste</span>
                <ArrowRight className="h-3.5 w-3.5 text-ink/30 group-hover:text-navy" />
              </button>
            ))}
          </div>
        </div>
      </section>

      <LeadsSidePanel
        open={drill.open}
        onClose={closeDrill}
        title={drill.title}
        leadIds={drill.ids}
        onSelectLead={onSelectLead}
      />
    </div>
  );
}
